/**
 * promotionEngine.js
 * Pure client-side promotion evaluation module.
 * All time logic uses the passed-in `now` Date (local time) — never UTC.
 */

// Convert JS getDay() (Sun=0..Sat=6) to ISO weekday (Mon=1..Sun=7)
function toISOWeekday(jsDay) {
  return jsDay === 0 ? 7 : jsDay;
}

// Get local HH:MM string from a Date
function toLocalHHMM(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// Get YYYY-MM-DD string from a Date using local time
function toLocalDateStr(date) {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

// Round to 2 decimal places
function round2(n) {
  return Math.round(n * 100) / 100;
}

// Return only the cart lines that fall within the promotion's scope
function getScopedLines(promotion, cart) {
  const { scope_type, scope_ids } = promotion;
  if (scope_type === 'all') return cart;
  if (scope_type === 'categories') return cart.filter(l => scope_ids && scope_ids.includes(l.category));
  if (scope_type === 'products') return cart.filter(l => scope_ids && scope_ids.includes(l.productCode));
  return [];
}

/**
 * Determines if a promotion is currently active (enabled + schedule check).
 * @param {object} promotion
 * @param {Date} now - local Date
 * @returns {boolean}
 */
export function isPromotionActive(promotion, now) {
  if (!promotion.enabled) return false;

  const { schedule_type } = promotion;

  if (schedule_type === 'always') {
    return true;
  }

  if (schedule_type === 'date_range') {
    const nowStr = toLocalDateStr(now);
    return nowStr >= promotion.start_date && nowStr <= promotion.end_date;
  }

  if (schedule_type === 'recurring') {
    const isoWeekday = toISOWeekday(now.getDay());
    if (!promotion.days_of_week || !promotion.days_of_week.includes(isoWeekday)) {
      return false;
    }
    const nowHHMM = toLocalHHMM(now);
    return nowHHMM >= promotion.start_time && nowHHMM <= promotion.end_time;
  }

  return false;
}

/**
 * Checks whether the cart satisfies the promotion's conditional gates
 * (min order value, dining option, scope coverage).
 * @param {object} promotion
 * @param {Array}  cart
 * @param {number} subtotal
 * @param {string} diningOption
 * @returns {boolean}
 */
export function meetsConditions(promotion, cart, subtotal, diningOption) {
  if (promotion.min_order_value != null && subtotal < promotion.min_order_value) {
    return false;
  }

  if (promotion.dining_option != null && promotion.dining_option !== diningOption) {
    return false;
  }

  // At least one cart line must fall within the scope
  const { scope_type, scope_ids } = promotion;
  if (scope_type === 'all') {
    return cart.length > 0;
  }
  if (scope_type === 'categories') {
    return cart.some(l => scope_ids && scope_ids.includes(l.category));
  }
  if (scope_type === 'products') {
    return cart.some(l => scope_ids && scope_ids.includes(l.productCode));
  }

  return true;
}

/**
 * Computes the discount amount (in currency units) for a single promotion.
 * Never returns a negative value; rounds to 2dp.
 * @param {object} promotion
 * @param {Array}  cart  - [{ productCode, category, quantity, unitPrice }]
 * @param {number} subtotal
 * @returns {number}
 */
export function computeDiscount(promotion, cart, subtotal) {
  const { rule_type, rule_config } = promotion;

  // ── order_percentage ──────────────────────────────────────────────────────
  if (rule_type === 'order_percentage') {
    if (!rule_config || rule_config.value == null || isNaN(rule_config.value)) return 0;
    const raw = subtotal * rule_config.value / 100;
    return round2(Math.min(raw, subtotal));
  }

  // ── order_fixed ───────────────────────────────────────────────────────────
  if (rule_type === 'order_fixed') {
    if (!rule_config || rule_config.value == null || isNaN(rule_config.value)) return 0;
    return round2(Math.min(rule_config.value, subtotal));
  }

  // ── item_percentage ───────────────────────────────────────────────────────
  if (rule_type === 'item_percentage') {
    if (!rule_config || rule_config.value == null || isNaN(rule_config.value)) return 0;
    const scopedLines = getScopedLines(promotion, cart);
    let total = 0;
    for (const line of scopedLines) {
      total += line.quantity * line.unitPrice * rule_config.value / 100;
    }
    return round2(Math.max(0, total));
  }

  // ── item_fixed ────────────────────────────────────────────────────────────
  // Fixed discount per line, capped at each line's total
  if (rule_type === 'item_fixed') {
    if (!rule_config || rule_config.value == null || isNaN(rule_config.value)) return 0;
    const scopedLines = getScopedLines(promotion, cart);
    let total = 0;
    for (const line of scopedLines) {
      const lineTotal = line.quantity * line.unitPrice;
      total += Math.min(rule_config.value, lineTotal);
    }
    return round2(Math.max(0, total));
  }

  // ── bogo ──────────────────────────────────────────────────────────────────
  // Discount the cheapest get_qty units per buy_qty purchased, by get_discount_pct
  if (rule_type === 'bogo') {
    if (!rule_config) return 0;
    const { buy_qty, get_qty, get_discount_pct } = rule_config;
    if (!buy_qty || !get_qty || get_discount_pct == null || isNaN(get_discount_pct)) return 0;
    const scopedLines = getScopedLines(promotion, cart);

    // Expand every unit into an array of unit prices
    const units = [];
    for (const line of scopedLines) {
      for (let i = 0; i < line.quantity; i++) {
        units.push(line.unitPrice);
      }
    }
    // Sort ascending so cheapest units are at the front (those are the "get" units)
    units.sort((a, b) => a - b);

    const cycleSize = buy_qty + get_qty;
    const cycles = Math.floor(units.length / cycleSize);

    let discount = 0;
    for (let c = 0; c < cycles; c++) {
      // Within each cycle the cheapest get_qty units are the "get" portion
      const start = c * cycleSize;
      for (let g = start; g < start + get_qty; g++) {
        discount += units[g] * get_discount_pct / 100;
      }
    }

    return round2(Math.max(0, discount));
  }

  // ── combo ─────────────────────────────────────────────────────────────────
  // All required items must be present; discount = qualifying sum − combo price
  if (rule_type === 'combo') {
    if (!rule_config) return 0;
    const { price: comboPrice, items: comboItems } = rule_config;
    if (!Array.isArray(comboItems) || comboItems.length === 0) return 0;

    let qualifyingSum = 0;
    for (const comboItem of comboItems) {
      const line = cart.find(l => comboItem.scope_ids.includes(l.productCode));
      if (!line || line.quantity < comboItem.qty) {
        return 0; // requirements not met
      }
      qualifyingSum += comboItem.qty * line.unitPrice;
    }

    const discount = qualifyingSum - comboPrice;
    return round2(Math.max(0, discount));
  }

  return 0;
}

/**
 * Evaluates all promotions against the cart state and returns:
 *   - autoApplied: { promotion, discount } | null  (highest-discount automatic promo)
 *   - manualOptions: [{ promotion, discount }]     (remaining eligible promos incl. manual-mode)
 *
 * @param {object} params
 * @param {Array}  params.cart
 * @param {number} params.subtotal
 * @param {string} params.diningOption
 * @param {Date}   params.now
 * @param {Array}  params.promotions
 * @returns {{ autoApplied: object|null, manualOptions: Array }}
 */
export function evaluatePromotions({ cart, subtotal, diningOption, now, promotions }) {
  const eligible = (promotions || [])
    .filter(p => isPromotionActive(p, now) && meetsConditions(p, cart, subtotal, diningOption))
    .map(p => ({ promotion: p, discount: computeDiscount(p, cart, subtotal) }))
    .filter(({ discount }) => discount > 0);

  const automatic = eligible.filter(({ promotion }) => promotion.application_mode === 'automatic');
  const manual    = eligible.filter(({ promotion }) => promotion.application_mode !== 'automatic');

  // Pick the automatic promo with the biggest discount; ties keep the first (FIFO) promo
  let autoApplied = null;
  if (automatic.length > 0) {
    autoApplied = automatic.reduce((best, curr) => curr.discount > best.discount ? curr : best);
  }

  // manualOptions = all manual promos + losing automatic promos, sorted by discount desc
  const losingAuto = autoApplied
    ? automatic.filter(({ promotion }) => promotion.promotion_id !== autoApplied.promotion.promotion_id)
    : automatic;

  const manualOptions = [...manual, ...losingAuto].sort((a, b) => b.discount - a.discount);

  return { autoApplied, manualOptions };
}
